import React from "react";
import { PublicDocShell } from "../components/PublicDocShell";
import "./CookiePolicyPage.css";

export const CookiePolicyPage: React.FC = () => {
  return (
    <PublicDocShell
      eyebrow="Legal & Compliance"
      title={
        <>
          Cookie <span className="public-doc-title-gradient">Policy</span>
        </>
      }
      subtitle="Last updated: December 28, 2024. This Cookie Policy explains how Kairos uses cookies and similar technologies on our website and services."
    >
      <div className="cookie-content">
        {/* Introduction */}
        <div className="cookie-section">
          <h2 className="cookie-section-title">
            <svg
              className="cookie-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            1. What Are Cookies?
          </h2>
          <div className="cookie-section-text">
            <p>
              Cookies are small text files that are stored on your device
              (computer, tablet, or mobile phone) when you visit our website or
              use our services. They help us provide you with a better
              experience and enable certain functionality.
            </p>
            <p>
              <strong>Purpose:</strong> Cookies allow us to remember your
              preferences, analyze how you use our services, and provide
              personalized content and advertisements.
            </p>
            <p>
              <strong>Types:</strong> We use various types of cookies, including
              essential cookies, performance cookies, functionality cookies, and
              targeting cookies.
            </p>
            <p>
              <strong>Consent:</strong> By using our website and services, you
              consent to our use of cookies in accordance with this Cookie
              Policy.
            </p>
          </div>
        </div>

        {/* Types of Cookies */}
        <div className="cookie-section">
          <h2 className="cookie-section-title">
            <svg
              className="cookie-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            2. Types of Cookies We Use
          </h2>
          <div className="cookie-section-text">
            <h3 className="cookie-subsection-title">2.1 Essential Cookies</h3>
            <p>
              These cookies are necessary for the website to function properly
              and cannot be disabled. They include:
            </p>
            <ul className="cookie-list">
              <li>
                <strong>Authentication Cookies:</strong> Remember your login
                status and session information
              </li>
              <li>
                <strong>Security Cookies:</strong> Protect against fraud and
                maintain security
              </li>
              <li>
                <strong>Load Balancing Cookies:</strong> Distribute traffic
                across our servers
              </li>
              <li>
                <strong>Functionality Cookies:</strong> Enable core website
                features
              </li>
            </ul>

            <h3 className="cookie-subsection-title">2.2 Performance Cookies</h3>
            <p>
              These cookies help us understand how visitors interact with our
              website by collecting and reporting information anonymously:
            </p>
            <ul className="cookie-list">
              <li>
                <strong>Analytics Cookies:</strong> Track page views, bounce
                rates, and user behavior
              </li>
              <li>
                <strong>Performance Monitoring:</strong> Monitor website speed
                and functionality
              </li>
              <li>
                <strong>Error Tracking:</strong> Identify and fix technical
                issues
              </li>
            </ul>

            <h3 className="cookie-subsection-title">
              2.3 Functionality Cookies
            </h3>
            <p>
              These cookies enable enhanced functionality and personalization:
            </p>
            <ul className="cookie-list">
              <li>
                <strong>Preference Cookies:</strong> Remember your language,
                region, and other settings
              </li>
              <li>
                <strong>Feature Cookies:</strong> Enable advanced features and
                customization
              </li>
              <li>
                <strong>Social Media Cookies:</strong> Integrate with social
                media platforms
              </li>
            </ul>

            <h3 className="cookie-subsection-title">2.4 Targeting Cookies</h3>
            <p>
              These cookies are used to deliver relevant advertisements and
              track marketing campaign effectiveness:
            </p>
            <ul className="cookie-list">
              <li>
                <strong>Advertising Cookies:</strong> Display personalized
                advertisements
              </li>
              <li>
                <strong>Retargeting Cookies:</strong> Show relevant content
                based on your interests
              </li>
              <li>
                <strong>Conversion Tracking:</strong> Measure the effectiveness
                of marketing campaigns
              </li>
            </ul>
          </div>
        </div>

        {/* Third-Party Cookies */}
        <div className="cookie-section">
          <h2 className="cookie-section-title">
            <svg
              className="cookie-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            3. Third-Party Cookies
          </h2>
          <div className="cookie-section-text">
            <p>
              We may use third-party services that place cookies on your device.
              These services help us:
            </p>
            <ul className="cookie-list">
              <li>
                <strong>Google Analytics:</strong> Analyze website traffic and
                user behavior
              </li>
              <li>
                <strong>Google Ads:</strong> Display relevant advertisements and
                track conversions
              </li>
              <li>
                <strong>Social Media Platforms:</strong> Enable social sharing
                and integration
              </li>
              <li>
                <strong>Customer Support Tools:</strong> Provide chat and
                support functionality
              </li>
              <li>
                <strong>Payment Processors:</strong> Process secure transactions
                (e.g., Stripe for subscription management)
              </li>
            </ul>
            <p>
              <strong>Third-Party Privacy Policies:</strong> These third-party
              services have their own privacy policies and cookie practices. We
              encourage you to review their policies for more information.
            </p>
            <p>
              <strong>Data Sharing:</strong> We do not sell your personal
              information to third parties. Third-party cookies are used solely
              to improve our services and provide relevant content.
            </p>
          </div>
        </div>

        {/* Cookie Management */}
        <div className="cookie-section">
          <h2 className="cookie-section-title">
            <svg
              className="cookie-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            4. Managing Your Cookie Preferences
          </h2>
          <div className="cookie-section-text">
            <h3 className="cookie-subsection-title">4.1 Browser Settings</h3>
            <p>
              You can control and manage cookies through your browser settings:
            </p>
            <ul className="cookie-list">
              <li>
                <strong>Chrome:</strong> Settings → Privacy and Security →
                Cookies and other site data
              </li>
              <li>
                <strong>Firefox:</strong> Options → Privacy & Security → Cookies
                and Site Data
              </li>
              <li>
                <strong>Safari:</strong> Preferences → Privacy → Manage Website
                Data
              </li>
              <li>
                <strong>Edge:</strong> Settings → Cookies and site permissions →
                Cookies and site data
              </li>
            </ul>

            <h3 className="cookie-subsection-title">4.2 Cookie Consent</h3>
            <p>
              When you first visit our website, you will see a cookie consent
              banner that allows you to:
            </p>
            <ul className="cookie-list">
              <li>Accept all cookies</li>
              <li>Reject non-essential cookies</li>
              <li>Customize your cookie preferences</li>
              <li>Learn more about our cookie practices</li>
            </ul>

            <h3 className="cookie-subsection-title">4.3 Opt-Out Options</h3>
            <p>You can opt out of certain types of cookies:</p>
            <ul className="cookie-list">
              <li>
                <strong>Analytics:</strong> Use browser add-ons to block
                analytics cookies
              </li>
              <li>
                <strong>Advertising:</strong> Opt out of personalized
                advertising through industry organizations
              </li>
              <li>
                <strong>Social Media:</strong> Adjust privacy settings on social
                media platforms
              </li>
            </ul>

            <h3 className="cookie-subsection-title">
              4.4 Impact of Disabling Cookies
            </h3>
            <p>
              <strong>Important Note:</strong> Disabling certain cookies may
              affect the functionality of our website and services. Essential
              cookies cannot be disabled without impacting core functionality,
              including authentication, job application tracking, and resume
              analysis features.
            </p>
          </div>
        </div>

        {/* Cookie Duration */}
        <div className="cookie-section">
          <h2 className="cookie-section-title">
            <svg
              className="cookie-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            5. Cookie Duration and Expiration
          </h2>
          <div className="cookie-section-text">
            <h3 className="cookie-subsection-title">5.1 Session Cookies</h3>
            <p>
              These cookies are temporary and are deleted when you close your
              browser. They are used for:
            </p>
            <ul className="cookie-list">
              <li>Maintaining your login session</li>
              <li>Remembering items in your job application workflow</li>
              <li>Temporary form data storage</li>
            </ul>

            <h3 className="cookie-subsection-title">5.2 Persistent Cookies</h3>
            <p>
              These cookies remain on your device for a set period or until
              manually deleted:
            </p>
            <ul className="cookie-list">
              <li>
                <strong>Preference Cookies:</strong> 1-2 years
              </li>
              <li>
                <strong>Analytics Cookies:</strong> 2-3 years
              </li>
              <li>
                <strong>Advertising Cookies:</strong> 1-2 years
              </li>
              <li>
                <strong>Authentication Cookies:</strong> 30 days to 1 year
              </li>
            </ul>

            <h3 className="cookie-subsection-title">5.3 Automatic Deletion</h3>
            <p>
              Cookies are automatically deleted when they expire. You can also
              manually delete cookies through your browser settings at any time.
            </p>
          </div>
        </div>

        {/* Data Collection */}
        <div className="cookie-section">
          <h2 className="cookie-section-title">
            <svg
              className="cookie-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            6. Information Collected Through Cookies
          </h2>
          <div className="cookie-section-text">
            <p>Cookies may collect the following types of information:</p>
            <ul className="cookie-list">
              <li>
                <strong>Device Information:</strong> IP address, browser type,
                operating system
              </li>
              <li>
                <strong>Usage Data:</strong> Pages visited, time spent, links
                clicked
              </li>
              <li>
                <strong>Preferences:</strong> Language, region, and other
                settings
              </li>
              <li>
                <strong>Authentication Data:</strong> Login status and session
                information
              </li>
              <li>
                <strong>Performance Metrics:</strong> Page load times and error
                rates
              </li>
              <li>
                <strong>Job Search Activity:</strong> Search queries, job views,
                and application interactions
              </li>
            </ul>
            <p>
              <strong>Personal Information:</strong> Cookies do not directly
              collect personal information such as names or phone numbers.
              However, they may be linked to personal information you provide
              through other means, such as when creating an account or
              submitting a job application.
            </p>
            <p>
              <strong>Aggregated Data:</strong> We use cookie data in aggregated
              form to improve our services and understand user behavior
              patterns, helping us enhance our AI-powered job matching and
              resume analysis features.
            </p>
          </div>
        </div>

        {/* Legal Basis */}
        <div className="cookie-section">
          <h2 className="cookie-section-title">
            <svg
              className="cookie-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l1-6m-1 6l-3-1m3 1l3 9a5.002 5.002 0 01-6.001 0M18 7l-3 9m3-9l-6-2m0-2v2m0 16V5m0 16H9m3-16h3"
              />
            </svg>
            7. Legal Basis for Cookie Use
          </h2>
          <div className="cookie-section-text">
            <h3 className="cookie-subsection-title">7.1 Consent</h3>
            <p>
              For non-essential cookies, we rely on your consent as the legal
              basis for processing. You can withdraw your consent at any time
              through our cookie management tools.
            </p>

            <h3 className="cookie-subsection-title">7.2 Legitimate Interest</h3>
            <p>
              For essential cookies and certain performance cookies, we rely on
              legitimate interest to provide and improve our services, including
              AI-powered job matching and resume analysis.
            </p>

            <h3 className="cookie-subsection-title">
              7.3 Contract Performance
            </h3>
            <p>
              Some cookies are necessary to perform our contractual obligations
              and provide the services you request, such as maintaining your job
              application tracking and subscription management.
            </p>

            <h3 className="cookie-subsection-title">7.4 Legal Obligation</h3>
            <p>
              We may use cookies to comply with legal requirements, such as
              maintaining security and preventing fraud.
            </p>
          </div>
        </div>

        {/* Updates and Changes */}
        <div className="cookie-section">
          <h2 className="cookie-section-title">
            <svg
              className="cookie-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            8. Updates to This Cookie Policy
          </h2>
          <div className="cookie-section-text">
            <p>
              We may update this Cookie Policy from time to time to reflect
              changes in our practices or for other operational, legal, or
              regulatory reasons.
            </p>
            <p>
              <strong>Notification of Changes:</strong> We will notify you of
              any material changes by:
            </p>
            <ul className="cookie-list">
              <li>Posting the updated policy on our website</li>
              <li>Sending you an email notification</li>
              <li>Displaying a prominent notice on our services</li>
            </ul>
            <p>
              <strong>Continued Use:</strong> Your continued use of our services
              after any changes constitutes acceptance of the updated Cookie
              Policy.
            </p>
            <p>
              <strong>Review:</strong> We encourage you to review this policy
              periodically to stay informed about how we use cookies.
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div className="cookie-section">
          <h2 className="cookie-section-title">
            <svg
              className="cookie-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            9. Contact Us
          </h2>
          <div className="cookie-section-text">
            <p>
              If you have any questions about this Cookie Policy or our use of
              cookies, please contact us:
            </p>
            <div className="cookie-contact-box">
              <p>
                <strong>Email:</strong> support@cognixisai.com
              </p>
              <p>
                <strong>Phone:</strong> (801) 306-5060
              </p>
            </div>
            <p>
              <strong>Data Protection Officer:</strong> For EU residents, you
              may also contact our Data Protection Officer at
              legal@cognixisai.com.
            </p>
            <p>
              <strong>Cookie Management:</strong> For technical questions about
              managing cookies, contact our technical support team.
            </p>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="cookie-section cookie-section-highlight-warning">
          <h2 className="cookie-section-title">
            <svg
              className="cookie-section-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            Legal Disclaimer
          </h2>
          <div className="cookie-section-text">
            <p>
              <strong>Important Legal Notice:</strong> This Cookie Policy is
              part of our comprehensive privacy framework and is incorporated by
              reference into our Terms of Service and Privacy Policy.
            </p>
            <p>
              <strong>Compliance:</strong> Our cookie practices comply with
              applicable data protection laws, including the General Data
              Protection Regulation (GDPR) and the California Consumer Privacy
              Act (CCPA).
            </p>
            <p>
              <strong>Enforcement:</strong> We reserve the right to enforce this
              Cookie Policy to the fullest extent permitted by law.
            </p>
            <p>
              <strong>Legal Advice:</strong> This policy is not intended to
              constitute legal advice. If you have questions about your legal
              rights, please consult with a qualified attorney.
            </p>
          </div>
        </div>
      </div>
    </PublicDocShell>
  );
};
